/**
 * SnapshotGallery — an event's frames as a picker beside one large preview.
 *
 * WHY NOT ImageCarousel (which this replaces in the event and alert records)
 * -------------------------------------------------------------------------
 * A carousel is right when there is one picture and occasionally a second. An AI event is the
 * other case: it carries the wide shot AND the crops taken out of it — full frame, person
 * crop, face crop, aligned face — and they are read TOGETHER. "What was cropped from what" is
 * the question, and a slide track answers it one frame at a time, with the others off-screen
 * behind arrows. So the frames stand in a column down the left, all visible at once, and the
 * one being read fills the panel on the right.
 *
 * THE KEYBOARD
 * ------------
 * One axis each, and neither depends on what has focus: LEFT/RIGHT move between EVENTS (the
 * RecordPager behind this modal owns those) and UP/DOWN move between this event's FRAMES.
 *
 * It was both axes on ← →, arbitrated by focus — the gallery took them once clicked into, the
 * record pager had them until then. That is invisible from the screen: the same key did one of
 * two things depending on where you last clicked, so a reader flicking through events would
 * suddenly find themselves flicking through crops instead, with nothing to say why. Separate
 * axes need no arbitration and no explaining, which is also why this claims UP/DOWN at the
 * window rather than only while focused.
 *
 * THE SMALL-CROP PROBLEM
 * ----------------------
 * A face crop is frequently 60-100px square. The carousel capped every image at its natural
 * size (`max-w-full max-h-full`), so opening one showed a postage stamp adrift in a large
 * black rectangle — the most important frame in a face event was the least readable thing in
 * the record. Here the preview SCALES A SMALL IMAGE UP to fill the panel, capped at
 * MAX_UPSCALE so a 64px crop becomes a legible 256px face rather than a blurred wall, and the
 * frame states the pixel size it actually has, so nobody reads the softness as a broken viewer.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

export interface GalleryImage {
  url: string;
  label?: string;
}

/**
 * How far a small image may be blown up in the preview.
 *
 * 4× turns a 64px face crop into 256px — the size at which a person is recognisable — and
 * stops before the point where the interpolation is all anyone can see. Anything already
 * larger than the panel is simply fitted to it; this cap only ever binds on crops.
 */
const MAX_UPSCALE = 4;

/** Natural pixel size of a loaded image, for the cap and for the caption. */
interface NaturalSize {
  w: number;
  h: number;
}

export interface SnapshotGalleryProps {
  images: GalleryImage[];
  /** Height of the preview panel — e.g. `h-[42vh]` in the event record's modal. */
  frameClassName?: string;
  /** Given, the preview becomes clickable — used to hand off to a lightbox. */
  onImageClick?: (image: GalleryImage, index: number) => void;
  /**
   * Which keys page the frames.
   *
   * `updown` (the default) leaves ← → to whatever steps through records — see THE KEYBOARD
   * above. `both` also takes ← →, for a gallery that is the only thing on screen that pages.
   * `off` binds nothing, for a caller with its own keyboard handling.
   */
  arrowKeys?: 'updown' | 'both' | 'off';
  /** A chip beside the frame's own type label — in practice, when it was captured. */
  badge?: ReactNode;
  /** Pinned bottom-right of the preview: the measurement the snapshot is evidence for. */
  overlay?: ReactNode;
  className?: string;
}

export default function SnapshotGallery({
  images = [],
  frameClassName,
  onImageClick,
  arrowKeys = 'updown',
  badge,
  overlay,
  className = '',
}: SnapshotGalleryProps) {
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Record<number, boolean>>({});
  const [natural, setNatural] = useState<Record<number, NaturalSize>>({});
  const touchStartX = useRef<number | null>(null);

  const count = images.length;
  const multiple = count > 1;

  // Clamp when the list shrinks under us (a reloaded event with fewer frames).
  useEffect(() => {
    setIndex((i) => (count === 0 ? 0 : Math.min(i, count - 1)));
  }, [count]);

  const go = useCallback(
    (delta: number) => {
      if (count === 0) return;
      setIndex((i) => (i + delta + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (!multiple || arrowKeys === 'off') return;
    const takesSideways = arrowKeys === 'both';
    const onKey = (e: KeyboardEvent) => {
      // Never from a text field, and never from a select — a dropdown's own up/down is what
      // the reader means there.
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      const back = e.key === 'ArrowUp' || (takesSideways && e.key === 'ArrowLeft');
      const fwd = e.key === 'ArrowDown' || (takesSideways && e.key === 'ArrowRight');
      if (!back && !fwd) return;
      // Up/Down would otherwise scroll the modal under the reader while the frame changes.
      e.preventDefault();
      go(back ? -1 : 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [multiple, go, arrowKeys]);

  if (count === 0) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  const current = images[index] || ({} as GalleryImage);
  const size = natural[index];
  const isFailed = !!failed[index];

  // Nothing here is focusable and it carries no ARROW_SCOPE_ATTR on purpose: that attribute
  // exists to take ← → away from a record pager, and this gallery no longer wants them — the
  // pager keeps that axis whether or not the reader has clicked a thumbnail.
  return (
    <div className={className}>
      {/* The picker is a fixed-width column and the preview takes the rest. `items-stretch` so
          the rail is exactly as tall as the frame beside it, however tall that is told to be. */}
      <div className="flex min-w-0 items-stretch gap-2.5">
        {multiple && (
          <div
            /* Scrolls rather than shrinks: four frames is the common case and they fit, but a
               model that starts emitting six must not squeeze the thumbnails to nothing. */
            className={`flex w-[5.25rem] flex-none flex-col gap-2 overflow-y-auto pr-0.5 sm:w-[6rem] ${frameClassName || ''}`}
          >
            {images.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                title={img.label || `Frame ${i + 1}`}
                aria-current={i === index}
                className={`group flex-none overflow-hidden rounded-lg border text-left transition-all ${
                  i === index
                    ? 'border-brand ring-1 ring-brand/60'
                    : 'border-border hover:border-muted-foreground/60'
                }`}
              >
                {/* object-cover in the picker (not contain): a thumbnail is for RECOGNISING a
                    frame, and a letterboxed crop in an 80px box is mostly black. */}
                <div className="flex h-14 items-center justify-center bg-black">
                  {failed[i] ? (
                    <span className="text-[9px] text-white/40">n/a</span>
                  ) : (
                    <img src={img.url} alt="" className="h-full w-full object-cover" />
                  )}
                </div>
                <div
                  className={`truncate border-t px-1.5 py-1 text-center text-[9px] font-bold uppercase leading-tight tracking-wider ${
                    i === index ? 'border-brand/40 text-brand' : 'border-border text-muted-foreground'
                  }`}
                >
                  {img.label || `Frame ${i + 1}`}
                </div>
              </button>
            ))}
          </div>
        )}

        <div
          className={`relative min-w-0 flex-1 overflow-hidden rounded-lg border border-border bg-black ${frameClassName || 'aspect-video'}`}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex h-full w-full items-center justify-center">
            {isFailed ? (
              <span className="text-[12px] text-white/50">Image unavailable</span>
            ) : (
              <img
                /* `key` so switching frames swaps the element rather than mutating one src —
                   without it the previous picture stays on screen, at the previous size, until
                   the new one decodes. */
                key={current.url}
                src={current.url}
                alt={current.label || `Snapshot ${index + 1}`}
                onLoad={(e) => {
                  const el = e.currentTarget;
                  setNatural((n) =>
                    n[index] ? n : { ...n, [index]: { w: el.naturalWidth, h: el.naturalHeight } },
                  );
                }}
                onError={() => setFailed((f) => ({ ...f, [index]: true }))}
                onClick={onImageClick ? () => onImageClick(current, index) : undefined}
                /* h-full w-full + object-contain fills the panel in BOTH directions — which is
                   what scales a small crop up — while the pixel cap keeps that from becoming a
                   wall of interpolation. Before the first load there is no size to cap by, so
                   the image is simply fitted. */
                className={`h-full w-full object-contain ${onImageClick ? 'cursor-zoom-in' : ''}`}
                style={size ? { maxWidth: size.w * MAX_UPSCALE, maxHeight: size.h * MAX_UPSCALE } : undefined}
              />
            )}
          </div>

          {/* What this frame IS, when it was taken, and how big it really is — read together,
              so one row of chips. The pixel size is here because an upscaled crop looks soft,
              and a reader who cannot see WHY assumes the viewer is broken. */}
          <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap items-center gap-1.5">
            {current.label && (
              <span className="rounded-md bg-black/75 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
                {current.label}
              </span>
            )}
            {badge && (
              <span className="rounded-md bg-black/75 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white/90 backdrop-blur-sm">
                {badge}
              </span>
            )}
            {size && (
              <span className="rounded-md bg-black/60 px-2 py-1 text-[10px] font-semibold tabular-nums text-white/70 backdrop-blur-sm">
                {size.w} × {size.h}
              </span>
            )}
          </div>

          {overlay && (
            <div className="pointer-events-none absolute bottom-3 right-3 rounded-lg bg-black/75 px-3 py-1.5 backdrop-blur-sm">
              {overlay}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
